import { CircularProgress, DialogContent, Divider } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import AppSimpleDialog from 'app/shared-components/dialogs/AppSimpleDialog';
import { ModalComponent } from 'app/shared-components/modals/modalManager';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useOfferByIdQuery } from 'src/app/queries/offers.queries';
import IQueryKeys from 'src/app/types/IQueryKeys';

const statusMessages = {
  NOT_STARTED: {
    title: 'Creating your merchant application',
    description:
      "We're preparing the required documents for the payment processor. This can take a little while, so feel free to sit back while we get everything ready.",
  },
  CREATING_APPLICATION: {
    title: 'Creating your merchant application',
    description:
      "We're preparing the required documents for the payment processor. This can take a little while, so feel free to sit back while we get everything ready.",
  },
  SENDING_APPLICATION: {
    title: 'Generating a secure application link',
    description:
      "You'll be redirected automatically once it's ready. A copy will also be sent to your email.",
  },
  WAITING_MERCHANT: {
    title: 'Retrieving your application link',
    description: "Just a moment, you'll be redirected shortly.",
  },
  WORK_IN_PROGRESS: {
    title: 'Retrieving your application link',
    description: "Just a moment, you'll be redirected shortly.",
  },
  FAILED: {
    title: 'Something went wrong',
    description: 'Please try again or contact support if the issue persists.',
  },
};

// In some cases the iframe loads North sessions from previous PayBlox web session
const LOGOUT_URL = 'https://apply.sbox.paymentshub.com/logout';

export const NorthOnboardingDialogContent: ModalComponent<{ offerId: number }> = ({
  open,
  onClose,
  props,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [pooling, setPooling] = useState(true);

  const { data, isLoading } = useOfferByIdQuery(props.offerId, {
    enabled: pooling, // Only poll when dialog is open and not in terminal states and offerId exists
    refetchInterval: 7500, // Poll every 7.5 seconds
    refetchIntervalInBackground: false, // Don't poll when tab is in background
    refetchOnWindowFocus: false, // Don't refetch on window focus
    retry: 3, // Reduce retry count
    retryDelay: 1000, // Fixed delay
    staleTime: 0, // Always consider data stale so it refetches
    gcTime: 0, // Don't cache this data to avoid conflicts
    networkMode: 'always', // Always try to fetch, even if offline
    // Add a unique query key modifier to avoid cache conflicts
    queryKeyHashFn: (queryKey) => JSON.stringify([...queryKey, 'north-processing-portal']),
  });
  const status = data?.data.northApplication?.status || 'NOT_STARTED';
  const iframeUrl = data?.data.northApplication?.merchantEnrollmentUrl;

  const [hasLoggedOut, setHasLoggedOut] = useState(false);
  const queryClient = useQueryClient();

  const handleOnClose = useCallback(() => {
    onClose();
    queryClient
      .invalidateQueries({ queryKey: [IQueryKeys.offersByIdKey] })
      .then(() => {
        console.log('invalidated offersByIdKey');
      })
      .catch(console.error);
  }, [onClose, queryClient]);

  const handleIframeLoad = () => {
    if (!hasLoggedOut) {
      setHasLoggedOut(true);
    }
  };

  useEffect(() => {
    if (
      [
        'WAITING_MERCHANT',
        'WORK_IN_PROGRESS',
        'MERCHANT_APPROVED',
        'SEND_TO_UNDERWRITING',
        'FINALIZED',
        'FAILED_TO_SEND_TO_UNDERWRITING',
        'FAILED',
        'NORTH_SYSTEM_ERROR',
      ].includes(status)
    ) {
      setPooling(false);
      console.log(`Pooling disabled, status: ${status}`);
    }

    if (['FAILED', 'NORTH_SYSTEM_ERROR', 'FAILED_TO_SEND_TO_UNDERWRITING'].includes(status)) {
      enqueueSnackbar('An unexpected issue has occurred.', {
        variant: 'errorToast',
      });
      handleOnClose();
      return;
    }
  }, [status, enqueueSnackbar, handleOnClose]);

  return (
    <AppSimpleDialog
      open={open}
      handleClose={() => handleOnClose()}
      title={pooling ? 'Processing Your Application' : 'Complete Your Application'}
      size={pooling ? 'sm' : 'lg'}
      scroll="paper"
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or escape key
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
          return;
        }
        handleOnClose();
      }}
    >
      <Divider />
      <DialogContent className="flex flex-col items-center gap-24 p-0">
        {pooling ? (
          <div className="flex flex-col justify-center items-center px-24 py-32 gap-24 min-h-[200px]">
            <CircularProgress size={48} className="text-blue-500" />
            {!isLoading && (
              <div className="text-center max-w-md">
                <h3 className="text-18 font-600 text-gray-900 mb-8 leading-tight">
                  {statusMessages[status as keyof typeof statusMessages]?.title ||
                    statusMessages.NOT_STARTED.title}
                </h3>
                <p className="text-14 font-400 text-gray-600 leading-relaxed">
                  {statusMessages[status as keyof typeof statusMessages]?.description ||
                    statusMessages.NOT_STARTED.description}
                </p>
              </div>
            )}
          </div>
        ) : (
          <iframe
            src={hasLoggedOut ? iframeUrl || LOGOUT_URL : LOGOUT_URL}
            className="w-full h-[80vh] min-h-[600px] max-h-[900px] border-0"
            onLoad={handleIframeLoad}
            title="North Application"
          />
        )}
      </DialogContent>
    </AppSimpleDialog>
  );
};
